import React, { useEffect, useState, useCallback } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, Box, Stack, Typography, List, ListItem, ListItemButton, Sheet, CircularProgress, Alert, Chip, IconButton, Tooltip } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import type { AdminMail } from '../../api/admin.ts';

interface Props {
  username: string | null;
  onClose: () => void;
}

export default function UserInboxModal({ username, onClose }: Props) {
  const { api } = useAdmin();
  const [mails, setMails] = useState<AdminMail[]>([]);
  const [selected, setSelected] = useState<AdminMail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback((preserveSelection: boolean) => {
    if (!username) return;
    setLoading(true); setError('');
    api.getUserInbox(username)
      .then(list => {
        setMails(list);
        if (!preserveSelection) setSelected(null);
        else if (selected) {
          const still = list.find(m => m.id === selected.id);
          setSelected(still || null);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [api, username, selected]);

  useEffect(() => {
    if (!username) return;
    setMails([]); setSelected(null); setError('');
    setLoading(true);
    api.getUserInbox(username)
      .then(list => setMails(list))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [username, api]);

  return (
    <Modal open={!!username} onClose={onClose}>
      <ModalDialog
        sx={{
          width: { xs: '95vw', md: '85vw' },
          maxWidth: 1200,
          height: '85vh',
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <DialogTitle sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>Inbox</span>
            <Typography component="span" color="primary" sx={{ fontWeight: 600 }}>{username}</Typography>
            <Chip size="sm">{mails.length}</Chip>
          </DialogTitle>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Tooltip title="Reload inbox">
              <IconButton variant="soft" size="sm" onClick={() => load(true)} disabled={loading}>
                {loading ? <CircularProgress size="sm" /> : '↻'}
              </IconButton>
            </Tooltip>
            <ModalClose sx={{ position: 'static' }} />
          </Stack>
        </Box>
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}><Alert color="danger">{error}</Alert></Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '320px 1fr', height: '100%' }}>
              <Sheet variant="plain" sx={{ borderRight: '1px solid', borderColor: 'divider', overflow: 'auto', bgcolor: 'background.surface' }}>
                {mails.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="neutral">No mails</Typography>
                  </Box>
                ) : (
                  <List size="sm" sx={{ '--ListItem-radius': 0 }}>
                    {mails.map(m => (
                      <ListItem key={m.id}>
                        <ListItemButton
                          selected={selected?.id === m.id}
                          onClick={() => setSelected(m)}
                          sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}
                        >
                          <Typography level="title-sm" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.subject || '(no subject)'}
                          </Typography>
                          <Typography level="body-xs" color="neutral" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.sender?.address}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {new Date(m.date).toLocaleString()}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Sheet>
              <Box sx={{ overflow: 'auto', p: 0 }}>
                {!selected ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: 'neutral.500' }}>
                    <Typography color="neutral">Select a mail to view</Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.body', zIndex: 1 }}>
                      <Typography level="title-md" sx={{ fontWeight: 700, mb: 0.5 }}>{selected.subject || '(no subject)'}</Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Typography level="body-sm"><b>From:</b> {selected.sender?.address}</Typography>
                        <Typography level="body-sm"><b>To:</b> {selected.receiver?.address}</Typography>
                        <Typography level="body-sm" color="neutral">{new Date(selected.date).toLocaleString()}</Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {selected.html ? (
                        <iframe
                          title={selected.id}
                          srcDoc={selected.html}
                          sandbox=""
                          style={{ width: '100%', minHeight: 500, border: '1px solid var(--joy-palette-divider)', borderRadius: 8, background: '#fff' }}
                        />
                      ) : (
                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
                          {selected.text || '(empty)'}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
